import { useGetList } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Database } from "lucide-react";

interface AuditEntry {
  id: string;
  table_name: string;
  record_id: string;
  action: string;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  user_id: string | null;
  created_at: string;
}

interface AuditTrailViewerProps {
  jobId: string;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

const ACTION_COLORS: Record<string, { color: string; bg: string }> = {
  INSERT: { color: "#16a34a", bg: "#dcfce7" },
  UPDATE: { color: "#2563eb", bg: "#dbeafe" },
  DELETE: { color: "#dc2626", bg: "#fee2e2" },
};

/**
 * Shows audit trail for a specific job by querying audit_log
 * where record_id = jobId OR new_data->>'job_id' = jobId.
 *
 * Note: For full cross-table job audit, a dedicated DB view or
 * function would be ideal. This uses a pragmatic approach.
 */
export function AuditTrailViewer({ jobId }: AuditTrailViewerProps) {
  // Get audit entries for the job record itself
  const { data: jobAudit, isPending } = useGetList<AuditEntry>("audit_log", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: { record_id: jobId },
  });

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold flex items-center gap-1.5">
        <Shield className="w-4 h-4" />
        Audit Trail
        {jobAudit && jobAudit.length > 0 && (
          <span className="text-xs text-muted-foreground font-normal">({jobAudit.length})</span>
        )}
      </h4>

      {isPending && <p className="text-xs text-muted-foreground">Loading…</p>}

      {!isPending && (!jobAudit || jobAudit.length === 0) && (
        <p className="text-xs text-muted-foreground">No audit records for this job.</p>
      )}

      {jobAudit && jobAudit.length > 0 && (
        <div className="space-y-1">
          {jobAudit.map((entry) => {
            const ac = ACTION_COLORS[entry.action] || ACTION_COLORS.UPDATE;
            return (
              <Card key={entry.id} className="shadow-none">
                <CardContent className="p-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs font-mono">{entry.table_name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded font-medium"
                        style={{ color: ac.color, backgroundColor: ac.bg }}>
                        {entry.action}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatTime(entry.created_at)}
                    </span>
                  </div>
                  {entry.action === "UPDATE" && entry.old_data && entry.new_data && (
                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {Object.keys(entry.new_data)
                        .filter((k) => {
                          const oldVal = JSON.stringify((entry.old_data as Record<string, unknown>)?.[k]);
                          const newVal = JSON.stringify((entry.new_data as Record<string, unknown>)?.[k]);
                          return oldVal !== newVal && k !== "updated_at";
                        })
                        .slice(0, 5)
                        .map((k) => (
                          <p key={k}>
                            <span className="font-medium">{k}</span>:{" "}
                            <span className="line-through text-red-400">
                              {JSON.stringify((entry.old_data as Record<string, unknown>)?.[k])?.substring(0, 40)}
                            </span>{" "}
                            →{" "}
                            <span className="text-green-600">
                              {JSON.stringify((entry.new_data as Record<string, unknown>)?.[k])?.substring(0, 40)}
                            </span>
                          </p>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
